import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import { FaEye, FaSort, FaSortUp, FaSortDown, FaSync, FaTimes, FaDownload, FaCog, FaArrowLeft } from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { searchFundRequests } from '~/services/fund-requests';
import { FundRequestFilters } from '~/types/fundrequests';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, parseVisibleColumns } from './fundRequestColumns';

// Human-readable labels for active filter chips
const FILTER_LABELS: Record<string, string> = {
    status: 'Status',
    client_name: 'Client Name',
    ntaf_id: 'NTAF ID',
    ap_invoice_number: 'AP Invoice #',
    payee: 'Payee',
    vendor_id: 'Vendor',
    expense_type: 'Expense Type',
    source: 'Source',
    amount_min: 'Min Amount',
    amount_max: 'Max Amount',
    created_date_start: 'Created From',
    created_date_end: 'Created To',
    updated_date_start: 'Updated From',
    updated_date_end: 'Updated To',
    coordinator: 'Coordinator',
    patient_id: 'Patient',
    fund_request_id: 'Fund Request #',
};

// Readable labels for known status values (falls back to prettified value)
const STATUS_LABELS: Record<string, string> = {
    open: 'Open',
    in_review: 'In Review',
    needs_info: 'Needs Info',
    ap_review: 'AP Review',
    for_dof_approval: 'For DOF Approval',
    appealed: 'Appealed',
    rejected: 'Rejected',
    paid: 'Paid',
    pending: 'Pending',
    submitted: 'Submitted',
    resubmitted: 'Resubmitted',
    draft: 'Draft',
};

// Turn a raw param value like "for_dof_approval" into "For Dof Approval"
const prettifyValue = (value: string): string =>
    value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());

interface FundRequestSearchResult {
    id: number;
    fund_name: string;
    ntaf_id: string;
    ap_invoice_number: string;
    vendor_name: string;
    vendor_id: number;
    request_amount: number;
    created_date: string;
    updated_date: string;
    submitted_date: string;
    fund_request_status: {
        id: number;
        label: string;
        value: string;
    };
    financial_coordinator: {
        id: number;
        first_name: string;
        last_name: string;
    } | null;
    priority: string;
    unread_notes_count: number;
    source: 'Web' | 'Manually Entered';
    submitted_by: string | null;
    had_needs_info: boolean;
}

const FundRequestSearchResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState<FundRequestSearchResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [refreshKey, setRefreshKey] = useState(0);

    // Column visibility state
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef<HTMLDivElement>(null);

    // Close column selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
                setShowColumnSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get visible columns in order
    const columns = useMemo(() => {
        return ALL_AVAILABLE_COLUMNS.filter(col => visibleColumnKeys.includes(col.key));
    }, [visibleColumnKeys]);

    const handleColumnToggle = (columnKey: string) => {
        setVisibleColumnKeys(prev => {
            if (prev.includes(columnKey)) {
                // Don't allow removing all columns
                if (prev.length === 1) return prev;
                return prev.filter(k => k !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleResetColumns = () => {
        setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMNS);
    };

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    // Function to render cell content based on column type
    const renderCellContent = (request: FundRequestSearchResult, columnKey: string) => {
        switch (columnKey) {
            case 'id':
                return request.id;
            case 'fund_name':
                return request.fund_name || '';
            case 'ntaf_id':
                return request.ntaf_id || '';
            case 'ap_invoice_number':
                return request.ap_invoice_number || '';
            case 'vendor_name':
                return request.vendor_name || '';
            case 'request_amount':
                return request.request_amount
                    ? `$${request.request_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '';
            case 'fund_request_status':
                return request.fund_request_status?.label || '';
            case 'source':
                return request.source || '';
            case 'submitted_by':
                return request.submitted_by || '';
            case 'financial_coordinator':
                if (request.financial_coordinator) {
                    return `${request.financial_coordinator.first_name || ''} ${request.financial_coordinator.last_name || ''}`.trim();
                }
                return '';
            case 'priority':
                return request.priority || '';
            case 'created_date':
                return request.created_date
                    ? new Date(request.created_date).toLocaleDateString()
                    : '';
            case 'updated_date':
                return request.updated_date
                    ? new Date(request.updated_date).toLocaleDateString()
                    : '';
            case 'submitted_date':
                return request.submitted_date
                    ? new Date(request.submitted_date).toLocaleDateString()
                    : '';
            case 'had_needs_info':
                return request.had_needs_info ? 'Yes' : 'No';
            default:
                return '';
        }
    };

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get page from URL params or default to 1
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                // Get sorting from URL params
                const sortBy = searchParams.get('sort_by');
                const sort = searchParams.get('sort');
                if (sortBy) {
                    setSortField(sortBy);
                    setSortDirection(sort === 'asc' ? 'asc' : 'desc');
                }

                // Build filters object from search params
                const filters: FundRequestFilters = {
                    status: searchParams.get('status') || '',
                    page: page,
                    coordinator: searchParams.get('coordinator') || null,
                    created_date_start: searchParams.get('created_date_start') || '',
                    created_date_end: searchParams.get('created_date_end') || '',
                    patient_id: searchParams.get('patient_id') || null,
                    vendor_id: searchParams.get('vendor_id') || null,
                    fund_request_id: searchParams.get('fund_request_id') || '',
                    source: (searchParams.get('source') as 'staff' | 'web' | '') || '',
                    sort: (sort as 'asc' | 'desc') || 'desc',
                    sort_by: sortBy || 'updated_date',
                };

                // Add additional search params as query params
                const additionalParams: Record<string, string> = {};
                const searchParamKeys = ['client_name', 'ntaf_id', 'ap_invoice_number', 'payee', 
                    'amount_min', 'amount_max', 'updated_date_start', 'updated_date_end'];
                
                searchParamKeys.forEach(key => {
                    const value = searchParams.get(key);
                    if (value) {
                        additionalParams[key] = value;
                    }
                });

                // Merge additional params with filters
                const mergedFilters = { ...filters, ...additionalParams };

                const response = await searchFundRequests(mergedFilters as FundRequestFilters);

                if (response.results) {
                    setResults(response.results);
                    setTotalCount(response.count || 0);
                    setTotalPages(Math.ceil((response.count || 0) / 25)); // Assuming 25 per page
                } else {
                    setResults([]);
                    setTotalCount(0);
                    setTotalPages(1);
                }
            } catch (err) {
                console.error('Error searching fund requests:', err);
                setError('Failed to search fund requests. Please try again.');
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handleSort = (sortKey: string) => {
        const newParams = new URLSearchParams(searchParams);
        const newDirection = sortField === sortKey && sortDirection === 'desc' ? 'asc' : 'desc';
        newParams.set('sort_by', sortKey);
        newParams.set('sort', newDirection);
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const handlePageChange = (page: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(page));
        setSearchParams(newParams);
    };

    const { clearFilterParams } = useReportFilters();
    const handleClearFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    const [exporting, setExporting] = useState(false);

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            // Fetch ALL results (not just current page) for export
            const filters: FundRequestFilters = {
                status: searchParams.get('status') || '',
                page: 1,
                coordinator: searchParams.get('coordinator') || null,
                created_date_start: searchParams.get('created_date_start') || '',
                created_date_end: searchParams.get('created_date_end') || '',
                patient_id: searchParams.get('patient_id') || null,
                vendor_id: searchParams.get('vendor_id') || null,
                fund_request_id: searchParams.get('fund_request_id') || '',
                source: (searchParams.get('source') as 'staff' | 'web' | '') || '',
                sort: (searchParams.get('sort') as 'asc' | 'desc') || 'desc',
                sort_by: searchParams.get('sort_by') || 'updated_date',
            };

            // Add additional search params
            const additionalParams: Record<string, string> = {};
            const searchParamKeys = ['client_name', 'ntaf_id', 'ap_invoice_number', 'payee', 
                'amount_min', 'amount_max', 'updated_date_start', 'updated_date_end'];
            
            searchParamKeys.forEach(key => {
                const value = searchParams.get(key);
                if (value) {
                    additionalParams[key] = value;
                }
            });

            // Fetch all pages
            let allResults: FundRequestSearchResult[] = [];
            let currentPage = 1;
            let hasMore = true;

            while (hasMore) {
                const mergedFilters = { ...filters, ...additionalParams, page: currentPage };
                const response = await searchFundRequests(mergedFilters as FundRequestFilters);
                
                if (response.results && response.results.length > 0) {
                    allResults = [...allResults, ...response.results];
                    // Check if there are more pages
                    hasMore = response.next !== null;
                    currentPage++;
                } else {
                    hasMore = false;
                }
            }

            // Generate CSV from all results
            const headers = columns.map(col => col.label);
            const rows = allResults.map(request =>
                columns.map(col => {
                    const content = renderCellContent(request, col.key);
                    // Escape quotes and wrap in quotes if contains comma
                    if (typeof content === 'string' && (content.includes(',') || content.includes('"'))) {
                        return `"${content.replace(/"/g, '""')}"`;
                    }
                    return content;
                })
            );

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `fund-requests-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting CSV:', error);
        } finally {
            setExporting(false);
        }
    };

    // Build the list of active filters (label + display value) for chips
    const getActiveFilters = () => {
        const filters: { key: string; label: string; value: string; displayValue: string }[] = [];
        const excludeKeys = ['page', 'sort', 'sort_by', 'visible_columns'];

        searchParams.forEach((value, key) => {
            if (excludeKeys.includes(key) || !value) return;

            const label = FILTER_LABELS[key] || prettifyValue(key);
            let displayValue = value;

            // Format specific values for better display
            if (key === 'status') {
                displayValue = STATUS_LABELS[value] || prettifyValue(value);
            } else if (key === 'source') {
                displayValue = value === 'web' ? 'Web' : value === 'staff' ? 'Manually Entered' : value;
            } else if (key.includes('date')) {
                displayValue = new Date(value + 'T00:00:00').toLocaleDateString();
            } else if (key === 'amount_min' || key === 'amount_max') {
                displayValue = `$${Number(value).toLocaleString()}`;
            } else if (key === 'expense_type') {
                displayValue = prettifyValue(value);
            }

            filters.push({ key, label, value, displayValue });
        });

        return filters;
    };

    // Remove a single active filter chip (handles multi-value params like expense_type)
    const removeFilter = (key: string, value?: string) => {
        const params = new URLSearchParams(searchParams);
        if (value && params.getAll(key).length > 1) {
            const values = params.getAll(key).filter(v => v !== value);
            params.delete(key);
            values.forEach(v => params.append(key, v));
        } else {
            params.delete(key);
        }
        params.set('page', '1');
        setSearchParams(params);
    };

    // Go back to the search form, preserving the current filters (but not paging/sort)
    const handleBackToSearch = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('sort');
        params.delete('sort_by');
        navigate(`/new-report?${params.toString()}`);
    };

    const activeFilters = getActiveFilters();

    const getSortIcon = (columnSortKey: string) => {
        if (sortField !== columnSortKey) {
            return <FaSort className="text-gray-400" />;
        }
        return sortDirection === 'asc' ? <FaSortUp className="text-primary" /> : <FaSortDown className="text-primary" />;
    };

    return (
        <>
            <Breadcrumb 
                pageName="Fund Request Report"
                parentPage="Reports"
                parentPath="/new-report"
            />

            <div className="rounded-sm border border-stroke bg-white shadow-default">
                {/* Header with actions */}
                <div className="border-b border-stroke px-6 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <h4 className="text-xl font-semibold text-black">
                                {loading ? 'Searching...' : `${totalCount} Results`}
                            </h4>
                            {activeFilters.length > 0 && (
                                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary">
                                    {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} applied
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                title="Refresh results"
                            >
                                <FaSync className={loading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                            <button
                                onClick={handleExportCSV}
                                disabled={results.length === 0 || exporting}
                                className="flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                                title="Export all results to CSV"
                            >
                                <FaDownload className={exporting ? 'animate-pulse' : ''} />
                                {exporting ? 'Exporting...' : 'Export All'}
                            </button>
                            <div className="relative" ref={columnSelectorRef}>
                                <button
                                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                                    className="flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                    title="Configure columns"
                                >
                                    <FaCog />
                                    Columns
                                </button>
                                {showColumnSelector && (
                                    <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-md border border-stroke bg-white p-4 shadow-lg">
                                        <div className="mb-3 flex items-center justify-between">
                                            <span className="font-medium">Show/Hide Columns</span>
                                            <button
                                                onClick={handleResetColumns}
                                                className="text-xs text-primary hover:underline"
                                            >
                                                Reset
                                            </button>
                                        </div>
                                        <div className="max-h-64 space-y-2 overflow-y-auto">
                                            {ALL_AVAILABLE_COLUMNS.map(col => (
                                                <label
                                                    key={col.key}
                                                    className="flex cursor-pointer items-center gap-2"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumnKeys.includes(col.key)}
                                                        onChange={() => handleColumnToggle(col.key)}
                                                        className="rounded border-gray-300"
                                                    />
                                                    <span className="text-sm">{col.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleBackToSearch}
                                className="flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                title="Back to search form (keeps your filters)"
                            >
                                <FaArrowLeft />
                                Back to Search
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                title="Clear all filters"
                            >
                                <FaTimes />
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Active filter chips */}
                    {activeFilters.length > 0 && (
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Filters:</span>
                            {activeFilters.map((filter, index) => (
                                <span
                                    key={`${filter.key}-${filter.value}-${index}`}
                                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                                >
                                    <span className="font-medium">{filter.label}:</span> {filter.displayValue}
                                    <button
                                        onClick={() => removeFilter(filter.key, filter.value)}
                                        className="ml-1 transition-colors hover:text-primary/70"
                                        title={`Remove ${filter.label} filter`}
                                    >
                                        <FaTimes size={12} />
                                    </button>
                                </span>
                            ))}
                            <button
                                onClick={handleClearFilters}
                                className="ml-2 text-sm text-gray-500 underline hover:text-gray-700"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* Error state */}
                {error && (
                    <div className="border-b border-stroke bg-red-50 px-6 py-4 text-red-600">
                        {error}
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    </div>
                )}

                {/* Results table */}
                {!loading && results.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                            <thead>
                                <tr className="bg-gray-2 text-left">
                                    {columns.map(column => (
                                        <th
                                            key={column.key}
                                            className="cursor-pointer px-4 py-4 font-medium text-black hover:bg-gray-3"
                                            onClick={() => handleSort(column.sortKey)}
                                        >
                                            <div className="flex items-center gap-2">
                                                {column.label}
                                                {getSortIcon(column.sortKey)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-4 py-4 font-medium text-black">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((request, index) => (
                                    <tr
                                        key={request.id}
                                        className={`border-b border-stroke hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-1'}`}
                                    >
                                        {columns.map(column => (
                                            <td key={column.key} className="px-4 py-4">
                                                {column.key === 'fund_name' ? (
                                                    <button
                                                        onClick={() => navigate(`/fund-requests/${request.id}`)}
                                                        className="font-medium text-primary hover:underline"
                                                    >
                                                        {renderCellContent(request, column.key)}
                                                    </button>
                                                ) : (
                                                    renderCellContent(request, column.key)
                                                )}
                                            </td>
                                        ))}
                                        <td className="px-4 py-4">
                                            <button
                                                onClick={() => navigate(`/fund-requests/${request.id}`)}
                                                className="flex items-center justify-center w-8 h-8 rounded text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors duration-200"
                                                title="View Fund Request Details"
                                            >
                                                <FaEye />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty state */}
                {!loading && results.length === 0 && !error && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <p className="text-gray-500">No fund requests found matching your criteria.</p>
                        <button
                            onClick={handleClearFilters}
                            className="mt-4 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90"
                        >
                            Clear Filters & Try Again
                        </button>
                    </div>
                )}

                {/* Pagination */}
                {!loading && results.length > 0 && totalPages > 1 && (
                    <div className="border-t border-stroke px-6 py-4">
                        <Pagination
                            page={currentPage}
                            pages={totalPages}
                            setPage={handlePageChange}
                            totalCount={totalCount}
                            pageSize={25}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default FundRequestSearchResults;
